import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { addToCart, getToken, removeToken, removeUser, addToWishlist, removeFromWishlist, getWishlist } from '../api/auth';
import { Heart } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BADGE_COLORS = {
  New:        { bg: '#1C1A18', color: '#fff' },
  Bestseller: { bg: '#B08D57', color: '#fff' },
  Sale:       { bg: '#C0392B', color: '#fff' },
  Limited:    { bg: '#2C2C2C', color: '#D4AF78' },
  Trending:   { bg: '#B08D57', color: '#fff' },
  Hot:        { bg: '#C0392B', color: '#fff' },
  Exclusive:  { bg: '#1C1A18', color: '#D4AF78' },
};

/* ── Build images array from all sources ── */
function getProductImages(p) {
  const fromFields = [p.image_1, p.image_2, p.image_3].filter(Boolean);
  const fromJsonb  = Array.isArray(p.images) ? p.images.filter(Boolean) : [];
  return [...new Set([...fromFields, ...fromJsonb])];
}

/* ── Image Swiper component ── */
const ImageSwiper = ({ p, bc }) => {
  const imgs = getProductImages(p);
  const count = imgs.length;
  const [cur, setCur] = useState(0);
  const touchStartX = useRef(null);

  const go = (dir, e) => {
    e.stopPropagation();
    setCur(c => (c + dir + count) % count);
  };

  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1, e);
    touchStartX.current = null;
  };

  return (
    <div className="bs-img-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>

      {/* Slide strip */}
      <div className="bs-slide-strip" style={{ transform: `translateX(-${cur * 100}%)` }}>
        {imgs.map((src, i) => (
          <img key={i} src={src} alt={`${p.name} ${i + 1}`} loading="lazy" />
        ))}
      </div>

      {/* Badge */}
      {bc && (
        <span className="bs-badge" style={{ background: bc.bg, color: bc.color }}>
          {p.badge}
        </span>
      )}

      {/* Sold Out */}
      {p.stock_qty === 0 && (
        <span style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(28,26,24,0.82)', color: '#fff',
          fontSize: 8, fontWeight: 700, letterSpacing: '0.18em',
          textTransform: 'uppercase', padding: '6px',
          textAlign: 'center', zIndex: 5,
        }}>Sold Out</span>
      )}

      {/* Prev / Next arrows — only when >1 image */}
      {count > 1 && (
        <>
          <button className="bs-sarrow bs-sarrow-l" onClick={e => go(-1, e)} aria-label="Previous image">&#8592;</button>
          <button className="bs-sarrow bs-sarrow-r" onClick={e => go(1, e)}  aria-label="Next image">&#8594;</button>
        </>
      )}

    </div>
  );
};

const Bestsellers = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [added, setAdded]       = useState({});
  const [wishlist, setWishlist] = useState(new Set());
  const navigate                = useNavigate();
  const { formatPrice }         = useCurrency();

  useEffect(() => {
    fetch(`${API}/products?collection=bestsellers&active=true`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setProducts(data.products.slice(0, 10));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    if (getToken()) {
      getWishlist()
        .then(items => setWishlist(new Set(items.map(i => i.id))))
        .catch(() => {});
    }
  }, []);

  const handleAdd = async (e, p) => {
  e.stopPropagation();
  if (!getToken()) { navigate('/account'); return; }

  const colorCount = [...new Set(p.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(p.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 || sizeCount > 1) {
    navigate(`/products/${p.id}`);
    return;
  }

  try {
    await addToCart(p.id, 1);
    setAdded(prev => ({ ...prev, [p.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [p.id]: false })), 1800);
    window.dispatchEvent(new Event('cart-updated'));
  } catch (err) {
    const msg = err.message?.toLowerCase() || '';
    if (msg.includes('invalid') || msg.includes('expired') || msg.includes('session') || msg.includes('token') || msg.includes('unauthorized')) {
      removeToken(); removeUser(); navigate('/account');
    }
  }
};

  const handleWishlist = async (id) => {
    if (!getToken()) { navigate('/account'); return; }
    const isIn = wishlist.has(id);
    setWishlist(prev => { const s = new Set(prev); isIn ? s.delete(id) : s.add(id); return s; });
    try {
      isIn ? await removeFromWishlist(id) : await addToWishlist(id);
    } catch {
      setWishlist(prev => { const s = new Set(prev); isIn ? s.add(id) : s.delete(id); return s; });
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        .bs-section {
          background: #fff;
          padding: 80px 0 70px;
        }

        /* ── Header ── */
        .bs-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding: 0 clamp(24px, 5vw, 80px);
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .bs-label {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .bs-label::before {
          content: '';
          width: 28px; height: 1px;
          background: #B08D57;
          display: inline-block;
        }
        .bs-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 4vw, 46px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.01em;
        }
        .bs-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .bs-viewall {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #fff;
          background: #1C1A18;
          text-decoration: none;
          padding: 13px 30px;
          display: inline-block;
          transition: background 0.22s;
          white-space: nowrap;
        }
        .bs-viewall:hover { background: #B08D57; }

        /* ── Grid ── */
        .bs-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 16px;
          margin: 0 clamp(24px, 5vw, 80px);
        }

        /* ── Card ── */
        .bs-card {
          background: #fff;
          position: relative;
          cursor: pointer;
          overflow: hidden;
        }
        .bs-card:hover { z-index: 2; }

        /* image */
        .bs-img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 3/4;
          overflow: hidden;
          background: #f9f6f1;
        }

        /* Slide strip */
        .bs-slide-strip {
          display: flex;
          width: 100%;
          height: 100%;
          transition: transform 0.35s ease;
        }
        .bs-slide-strip img {
          flex: 0 0 100%;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
        }

        /* Swipe arrows */
        .bs-sarrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          border-radius: 50%;
          color: #666;
          font-size: 12px;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: all 0.2s ease;
          z-index: 10;
        }
        .bs-img-wrap:hover .bs-sarrow { opacity: 1; }
        .bs-sarrow-l { left: 8px; }
        .bs-sarrow-r { right: 8px; }
        .bs-sarrow:hover { background: #fff; color: #1C1A18; }

        .bs-card:hover .bs-img-wrap img { transform: scale(1.05); }

        /* badge */
        .bs-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 4px 10px;
          z-index: 2;
        }

        /* Wishlist Heart */
        .bs-heart {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 32px;
          height: 32px;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: all 0.2s ease;
        }
        .bs-heart:hover { transform: scale(1.1); background: #fff; }
        .bs-heart svg { width: 16px; height: 16px; color: #9A9186; transition: color 0.2s; }
        .bs-heart--on svg { color: #C0392B; }

        /* body */
        .bs-body { padding: 12px 14px 14px; }
        .bs-name {
          font-family: 'Jost', sans-serif;
          font-size: 12.5px;
          font-weight: 500;
          color: #1C1A18;
          margin-bottom: 6px;
          line-height: 1.35;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: pointer;
          transition: color 0.2s;
        }
        .bs-name:hover { color: #B08D57; }
        .bs-prices {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin-bottom: 8px;
        }
        .bs-price { font-family: 'Jost', sans-serif; font-size: 15px; font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
        
        .bs-original {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          color: #b0a99e;
          text-decoration: line-through;
        }
        .bs-off {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          color: #C0392B;
          letter-spacing: 0.04em;
        }

        /* add to cart */
        .bs-atc {
          width: 100%;
          background: #1C1A18;
          border: none;
          color: #fff;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 10px;
          cursor: pointer;
          transition: background 0.22s;
          display: block;
        }
        .bs-atc:hover { background: #B08D57; }
        .bs-atc.added { background: #8A6D3E; }

        /* loading */
        .bs-loading {
          text-align: center;
          padding: 60px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: #9A9186;
          letter-spacing: 0.1em;
        }

        @media (max-width: 900px) {
          .bs-grid { grid-template-columns: repeat(3, 1fr); gap: 14px; }
        }
        @media (max-width: 600px) {
          .bs-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            margin: 0 clamp(12px, 4vw, 24px);
          }
          .bs-section { padding: 56px 0 52px; }
          .bs-name { font-size: 12px; white-space: normal; }
        }
      `}</style>

      <section className="bs-section">

        {/* Header */}
        <div className="bs-header">
          <div>
            <p className="bs-label">Top Picks</p>
            <h2 className="bs-title">Best <em>Sellers</em></h2>
          </div>
          <Link to="/collections/bestsellers" className="bs-viewall">View All</Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="bs-loading">Loading…</div>
        ) : products.length === 0 ? (
          <div className="bs-loading">No bestsellers yet.</div>
        ) : (
          <div className="bs-grid">
            {products.map((p, i) => {
              const hasDiscount  = p.sale_price && p.sale_price < p.original_price;
              const displayPrice = hasDiscount ? p.sale_price : p.original_price;
              const mrpPrice     = hasDiscount ? p.original_price : null;
              const discount     = mrpPrice
                ? Math.round((1 - displayPrice / mrpPrice) * 100)
                : 0;
              const bc = p.badge
                ? (BADGE_COLORS[p.badge] || { bg: '#1C1A18', color: '#fff' })
                : null;

              return (
                <motion.div
                  key={p.id}
                  className="bs-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: (i % 5) * 0.07 }}
                  viewport={{ once: true, amount: 0.1 }}
                  onClick={() => navigate(`/products/${p.id}`, {
                    state: { collectionName: 'Bestsellers', collectionSlug: 'bestsellers' }
                  })}
                >
                  {/* Image */}
                  <div className="bs-img-wrap">
                    <ImageSwiper p={p} bc={bc} />

                    {/* Wishlist Heart */}
                    <button
                      className={`bs-heart${wishlist.has(p.id) ? ' bs-heart--on' : ''}`}
                      onClick={e => { e.stopPropagation(); handleWishlist(p.id); }}
                      aria-label={wishlist.has(p.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    >
                      <Heart fill={wishlist.has(p.id) ? 'currentColor' : 'none'} />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="bs-body">
                    <div className="bs-name">{p.name}</div>
                    <div className="bs-prices">
                      <span className="bs-price">
                        {formatPrice(displayPrice)}
                      </span>
                      {mrpPrice && (
                        <span className="bs-original">
                          {formatPrice(mrpPrice)}
                        </span>
                      )}
                      {discount > 0 && (
                        <span className="bs-off">{discount}% Off</span>
                      )}
                    </div>
                   <button
                    className={`bs-atc${added[p.id] ? ' added' : ''}`}
                    onClick={e => { e.stopPropagation(); handleAdd(e, p); }}
                    disabled={p.stock_qty === 0}
                    style={p.stock_qty === 0 ? { background: '#9A9186', cursor: 'not-allowed' } : {}}
                  >
                    {added[p.id] ? '✓ Added to Cart'
                      : p.stock_qty === 0 ? 'Sold Out'
                      : ([...new Set(p.colors?.filter(c => c && c.trim()) ?? [])].length > 1 ||
                      [...new Set(p.sizes?.filter(s => s && s.trim()) ?? [])].length > 1) ? 'Select Options'
                      : 'Add to Cart'}
                  </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

      </section>
    </>
  );
};

export default Bestsellers;